import _ from 'lodash';
import { defineStore, storeToRefs } from 'pinia';

import { HITL, Ringtone } from '@/api';
import { NotifySettings, SettingsObj } from '@/types';
import { useHelpers } from '@/composables';
import { SETTINGS_CATEGORIES, DEFAULT_NOTIFICATIONS_SETTINGS } from '@/constants';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

export const useNotifySettingsStore = defineStore('HITL-Settings-Notify', {
  state: () => {
    return {
      isReady: false,
      isUpdating: false,
      notifications: DEFAULT_NOTIFICATIONS_SETTINGS as NotifySettings,
    };
  },
});

export const useNotifySettingsActions = () => {
  const store = useNotifySettingsStore();

  const initializeNotifySettings = async (options: {
    isCommon?: boolean;
    force?: boolean;
  } = {}) => {
    try {
      if (store.isReady && !options.force) {
        return;
      }

      store.isReady = false;
      const response = await HITL.settings.getSettings({
        isCommon: !!options.isCommon,
        categories: [SETTINGS_CATEGORIES.NOTIFICATIONS],
      });

      const settings = response.data[SETTINGS_CATEGORIES.NOTIFICATIONS] as SettingsObj;
      const notifications = settings?.data as NotifySettings;
      if (!_.isEmpty(notifications)) {
        store.notifications = notifications;
      }

      store.isReady = true;
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const updateNotifySettings = (notifications: NotifySettings) => {
    store.notifications = notifications;
  };

  const updateAPINotifySettings = async (options: { isCommon?: boolean; } = {}) => {
    try {
      store.isUpdating = true;
      const request = {
        isCommon: !!options.isCommon,
        data: { [SETTINGS_CATEGORIES.NOTIFICATIONS]: store.notifications },
      };

      await HITL.settings.updateSettings(request);
      if (!options.isCommon) {
        applyNotificationsSettings({ data: store.notifications });
      }

      store.isUpdating = false;
    } catch (error) {
      store.isUpdating = false;
      console.error('Error while updating notifications settings!', error);
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const applyNotificationsSettings = (model: { data?: NotifySettings; }) => {
    const { humanInTheLoopURL, serviceDiscoveryURL } = useHelpers();

    if (!model || !model.data) {
      return Ringtone.init({
        notifications: DEFAULT_NOTIFICATIONS_SETTINGS,
        humanInTheLoopURL: humanInTheLoopURL.value,
        serviceDiscoveryURL: serviceDiscoveryURL.value,
      });
    }

    const notifications = model.data;
    if (!store.isReady) {
      store.notifications = notifications;
      store.isReady = true;
    }

    Ringtone.init({
      notifications,
      humanInTheLoopURL: humanInTheLoopURL.value,
      serviceDiscoveryURL: serviceDiscoveryURL.value,
    });
  };

  return {
    initializeNotifySettings,
    applyNotificationsSettings,
    updateNotifySettings,
    updateAPINotifySettings,
  };
};

export const useNotifySettings = () => {
  return {
    ...storeToRefs(useNotifySettingsStore()),
    ...useNotifySettingsActions(),
  };
};
